import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Card, Button } from './ui';
import {
  previewFoaRegeneration,
  executeFoaRegeneration,
  FoaRegenerationPreview,
  FoaRegenerationResult,
  FoaPreviewClaim,
} from '../api/foaRegeneration';

const AdminFoaRegeneration: React.FC = () => {
  const { isAdmin } = useAuth();

  const [email, setEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [preview, setPreview] = useState<FoaRegenerationPreview | null>(null);
  const [selectedClaimIds, setSelectedClaimIds] = useState<Set<string>>(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<FoaRegenerationResult | null>(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const resetState = () => {
    setPreview(null);
    setSelectedClaimIds(new Set());
    setShowConfirm(false);
    setResult(null);
    setError('');
    setSuccessMessage('');
  };

  const handleSearch = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Please enter a client email address.');
      return;
    }

    resetState();
    setSearching(true);

    try {
      const data = await previewFoaRegeneration(trimmed);
      setPreview(data);
      // Select all eligible claims by default
      const ids = new Set<string>(data.claims.map((c: FoaPreviewClaim) => c.id));
      setSelectedClaimIds(ids);
      if (data.claims.length === 0) {
        setError('No eligible claims found for this client.');
      }
    } catch (err: any) {
      console.error('FOA regeneration preview failed:', err);
      setError(err.response?.data?.error || 'Failed to search for client. Please check the email and try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleToggleClaim = (claimId: string) => {
    setSelectedClaimIds(prev => {
      const next = new Set(prev);
      if (next.has(claimId)) {
        next.delete(claimId);
      } else {
        next.add(claimId);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    if (!preview) return;
    if (selectedClaimIds.size === preview.claims.length) {
      setSelectedClaimIds(new Set());
    } else {
      setSelectedClaimIds(new Set(preview.claims.map(c => c.id)));
    }
  };

  const handleRegenerateClick = () => {
    if (selectedClaimIds.size === 0) {
      setError('Please select at least one claim.');
      return;
    }
    setError('');
    setShowConfirm(true);
  };

  const handleConfirmRegenerate = async () => {
    setShowConfirm(false);
    setProcessing(true);
    setError('');
    setSuccessMessage('');
    setResult(null);

    try {
      const data = await executeFoaRegeneration(
        email.trim(),
        Array.from(selectedClaimIds)
      );
      setResult(data);
      setSuccessMessage(
        `Regeneration complete: ${data.succeeded} succeeded, ${data.failed} failed out of ${data.totalProcessed} processed.`
      );
    } catch (err: any) {
      console.error('FOA regeneration execution failed:', err);
      setError(err.response?.data?.error || 'Failed to regenerate FOA documents. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr: string): string => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  if (!isAdmin) {
    return <div className="status-message" style={{ color: '#dc3545' }}>Admin access required.</div>;
  }

  return (
    <div className="foa-regen-management">
      <style>{`
        .foa-regen-management h2 {
          color: #2c3e50;
          margin-bottom: 20px;
        }

        .foa-section {
          margin-bottom: 24px;
        }

        .foa-section .description {
          color: #666;
          font-style: italic;
          font-size: 14px;
          margin: 0 0 16px 0;
        }

        .foa-alert {
          padding: 12px 16px;
          margin-bottom: 16px;
          border-radius: 4px;
          font-size: 14px;
        }

        .foa-alert-error {
          background-color: #f8d7da;
          border: 1px solid #f5c6cb;
          color: #721c24;
        }

        .foa-alert-success {
          background-color: #d4edda;
          border: 1px solid #c3e6cb;
          color: #155724;
        }

        .foa-search-row {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .foa-search-input {
          flex: 1;
          max-width: 400px;
          padding: 10px 14px;
          border: 1px solid #ced4da;
          border-radius: 4px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;
        }

        .foa-search-input:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.15);
        }

        .foa-btn {
          padding: 10px 20px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: bold;
          font-size: 14px;
          transition: background-color 0.2s, opacity 0.2s;
        }

        .foa-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .foa-btn-primary {
          background-color: #007bff;
          color: white;
        }

        .foa-btn-primary:hover:not(:disabled) {
          background-color: #0069d9;
        }

        .foa-btn-danger {
          background-color: #dc3545;
          color: white;
        }

        .foa-btn-danger:hover:not(:disabled) {
          background-color: #c82333;
        }

        .foa-btn-cancel {
          background-color: #6c757d;
          color: white;
        }

        .foa-btn-cancel:hover:not(:disabled) {
          background-color: #5a6268;
        }

        .foa-client-info {
          margin-bottom: 16px;
          padding: 12px 16px;
          background-color: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          font-size: 14px;
          color: #495057;
        }

        .foa-client-info strong {
          color: #2c3e50;
        }

        .foa-table-wrapper {
          overflow-x: auto;
          margin-bottom: 16px;
        }

        .foa-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        .foa-table th,
        .foa-table td {
          padding: 10px 12px;
          text-align: left;
          border-bottom: 1px solid #e9ecef;
        }

        .foa-table th {
          background-color: #f8f9fa;
          color: #495057;
          font-weight: 600;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          white-space: nowrap;
        }

        .foa-table tr:hover {
          background-color: #f8f9fa;
        }

        .foa-table input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
        }

        .foa-button-row {
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
        }

        .foa-confirm-prompt {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .foa-confirm-prompt .confirm-text {
          color: #dc3545;
          font-weight: 500;
          font-size: 14px;
        }

        .foa-processing-indicator {
          margin-top: 12px;
          color: #007bff;
          font-size: 14px;
          font-style: italic;
        }

        .foa-results-list {
          margin-top: 16px;
        }

        .foa-result-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 10px 12px;
          margin-bottom: 8px;
          border-radius: 4px;
          font-size: 14px;
        }

        .foa-result-item.success {
          background-color: #d4edda;
          border: 1px solid #c3e6cb;
          color: #155724;
        }

        .foa-result-item.failure {
          background-color: #f8d7da;
          border: 1px solid #f5c6cb;
          color: #721c24;
        }

        .foa-result-icon {
          font-weight: bold;
          font-size: 16px;
          flex-shrink: 0;
          width: 20px;
          text-align: center;
        }

        .foa-result-details {
          flex: 1;
        }

        .foa-result-details .claim-id {
          font-weight: 600;
        }

        .foa-result-details .error-msg {
          margin-top: 4px;
          font-size: 13px;
          opacity: 0.9;
        }

        .foa-selected-count {
          font-size: 13px;
          color: #6c757d;
        }
      `}</style>

      <PageHeader title="FOA Document Regeneration" />

      {error && <div className="foa-alert foa-alert-error">{error}</div>}
      {successMessage && <div className="foa-alert foa-alert-success">{successMessage}</div>}

      {/* Search Section */}
      <Card title="Search Client" className="foa-section">
        <p className="description">
          Enter a client email address to find their claims and regenerate FOA documents.
        </p>

        <div className="foa-search-row">
          <input
            type="email"
            className="foa-search-input"
            placeholder="client@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !searching) handleSearch();
            }}
            disabled={searching || processing}
          />
          <Button
            variant="primary"
            onClick={handleSearch}
            disabled={searching || processing}
          >
            {searching ? 'Searching...' : 'Search'}
          </Button>
        </div>
      </Card>

      {/* Preview Section */}
      {preview && preview.claims.length > 0 && !result && (
        <Card title="Eligible Claims" className="foa-section">

          <div className="foa-client-info">
            <div><strong>Client:</strong> {preview.clientName}</div>
            <div><strong>Email:</strong> {preview.clientEmail}</div>
            <div>
              <strong>Claims:</strong> {preview.eligibleClaims} eligible out of {preview.totalClaims} total
            </div>
          </div>

          <div className="foa-table-wrapper">
            <table className="foa-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedClaimIds.size === preview.claims.length}
                      onChange={handleToggleAll}
                      disabled={processing}
                    />
                  </th>
                  <th>Claim ID</th>
                  <th>Lender</th>
                  <th>Vehicle</th>
                  <th>Status</th>
                  <th>Last Submission</th>
                  <th>Submissions</th>
                </tr>
              </thead>
              <tbody>
                {preview.claims.map((claim: FoaPreviewClaim) => (
                  <tr key={claim.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedClaimIds.has(claim.id)}
                        onChange={() => handleToggleClaim(claim.id)}
                        disabled={processing}
                      />
                    </td>
                    <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>{claim.id}</td>
                    <td>{claim.lenderName}</td>
                    <td>{claim.vehicle}</td>
                    <td>{claim.status}</td>
                    <td>{claim.lastSubmission ? formatDate(claim.lastSubmission.submittedAt) : '-'}</td>
                    <td style={{ textAlign: 'center' }}>{claim.totalSubmissions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="foa-button-row">
            {!showConfirm ? (
              <>
                <Button
                  variant="primary"
                  onClick={handleRegenerateClick}
                  disabled={processing || selectedClaimIds.size === 0}
                >
                  Regenerate FOA Documents
                </Button>
                <span className="foa-selected-count">
                  {selectedClaimIds.size} of {preview.claims.length} claims selected
                </span>
              </>
            ) : (
              <div className="foa-confirm-prompt">
                <span className="confirm-text">
                  Are you sure? This will regenerate {selectedClaimIds.size} document{selectedClaimIds.size !== 1 ? 's' : ''}.
                </span>
                <Button
                  variant="danger"
                  onClick={handleConfirmRegenerate}
                  disabled={processing}
                >
                  Yes, Regenerate
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowConfirm(false)}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>

          {processing && (
            <div className="foa-processing-indicator">
              Regenerating FOA documents... This may take a moment.
            </div>
          )}
        </Card>
      )}

      {/* Results Section */}
      {result && (
        <Card title="Regeneration Results" className="foa-section">
          <p className="description">
            Processed {result.totalProcessed} claim{result.totalProcessed !== 1 ? 's' : ''}: {result.succeeded} succeeded, {result.failed} failed.
          </p>

          <div className="foa-results-list">
            {result.results.map(item => (
              <div
                key={item.claimId}
                className={`foa-result-item ${item.success ? 'success' : 'failure'}`}
              >
                <span className="foa-result-icon">
                  {item.success ? '\u2713' : '\u2717'}
                </span>
                <div className="foa-result-details">
                  <span className="claim-id">{item.claimId}</span>
                  {item.success && item.regenerationId && (
                    <span style={{ marginLeft: 8, fontSize: 13, opacity: 0.8 }}>
                      (ID: {item.regenerationId})
                    </span>
                  )}
                  {!item.success && item.error && (
                    <div className="error-msg">{item.error}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 16 }}>
            <Button
              variant="primary"
              onClick={() => {
                resetState();
                setEmail('');
              }}
            >
              Start New Search
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AdminFoaRegeneration;
